import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {NgFor, NgIf} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {GetValueAccessorProvider, ValueAccessorBase} from '../common/value-accessor-base';
import {LanguageService} from '../../../original-services/language.service';
import {Utils} from 'app/common/utils';
import {IconsaxDirective} from '../../../directives/iconsax.directive';
import {normalizeSearchText} from "../../../services/utils/normalize-search.utils";
import {UiButtonComponent} from '../ui-button/ui-button.component';

export enum TextBoxFormatType {
    None,
    Number,
}

@Component({
    selector: 'ui-text-box',
    templateUrl: './text-box.html',
    styleUrls: ['./text-box.scss'],
    providers: [GetValueAccessorProvider(TextBoxComponent)],
    imports: [NgIf, NgFor, FormsModule, IconsaxDirective, UiButtonComponent]
})
export class TextBoxComponent extends ValueAccessorBase<string> implements OnInit {
    @Input() iconClass = '';
    @Input() placeholder = 'placeholder';
    @Input() name = '';
    @Input() inputId?: string;
    @Input() type = 'text';
    @Input() invalid = false;
    @Input() notNative: boolean;
    @Input() readonly = false;
    @Input() eyeMode = false;
    @Input() tabindex = 0;
    @Input() autocompleteItems: string[] = [];
    @Input() formatType: TextBoxFormatType = TextBoxFormatType.None;
    @Input() autocomplete = 'off';
    @Input() darkField;
    @Input() showMagnifier = false;
    @Input() showClearButton = false;
    @Input() dataTestId: string | null = null;
    @Input() minDecimalPlaces?: number;
    @Input() normalized = false;
    @Output() onFocus = new EventEmitter<boolean>();
    @Output() onFocusOut = new EventEmitter<boolean>();
    @Output() onBlur = new EventEmitter<boolean>();
    @Output() onKeydown = new EventEmitter<any>();
    /**
     * Escape pressed while the field is focused. Emitted raw and *without* stopping propagation:
     * the box is used inside dialogs that close on `document:keydown.esc`, so swallowing it here
     * would break them. Consumers that want to keep Escape local call `stopPropagation()` in their
     * own handler — still effective, since `emit` runs inside the original dispatch.
     */
    @Output() onEscape = new EventEmitter<KeyboardEvent>();
    @Output() onChange = new EventEmitter<any>();
    @Output() onValue = new EventEmitter<any>();
    @Output() onClear = new EventEmitter<any>();
    @ViewChild('input') input: ElementRef;
    @ViewChild('autocompleteList') autocompleteList: ElementRef;
    firstBlur = true;
    showSuggestion = false;
    filteredSuggestions: string[] = [];
    isPasswordShow = false;

    constructor(private languageService: LanguageService) {
        super()
    }

    ngOnInit() {
    }

    eyeClick() {
        this.isPasswordShow = this.input.nativeElement.type === 'password';
        this.input.nativeElement.type = this.isPasswordShow ? 'text' : 'password';
    }

    writeValue(value: string) {
        if (this.formatType === TextBoxFormatType.Number) {
            super.writeValue(Utils.floatToString(value, this.languageService.getLocale(), this.minDecimalPlaces));
        } else {
            super.writeValue(value);
        }
    }

    inputValueChanged(value: string) {
        if (this.normalized) {
            value = normalizeSearchText(value);
        }
        this.filterSuggestions(value);
        if (this.formatType === TextBoxFormatType.Number) {
            this.value = Utils.stringToFloat(value, this.languageService.getLocale());
        } else {
            this.value = value;
        }
        this.onValue.emit(this.value);
    }

    focus() {
        this.input.nativeElement.focus();
    }

    /**
     * `OnDemandControl` hook (see `app/common/edit-on-demand-cells.ts`): a list cell mounts the box
     * on click and expects it focused, with the old value selected so typing replaces it.
     */
    open(): void {
        this.input?.nativeElement?.focus();
        this.input?.nativeElement?.select();
    }

    focusOut() {
        this.input.nativeElement.blur();
    }

    onInputFocus(e?: any) {
        if (this.autocompleteItems.length && !this.filteredSuggestions.length) {
            this.filteredSuggestions = JSON.parse(JSON.stringify(this.autocompleteItems));
        }
        this.showSuggestion = !!this.filteredSuggestions.length;
        e.stopPropagation();
        e.preventDefault();
        this.onFocus.emit(true);
    }

    onInputChange(event: any): void {
        this.onChange.emit(event);
    }

    onInputBlur(e?: any) {
        if (this.autocompleteItems.length) {
            setTimeout(() => {
                this.showSuggestion = false;
            }, 200)
        } else {
            e.stopPropagation();
            e.preventDefault();
            this.onBlur.emit(e);
        }
    }

    clearField() {
        this.onClear.emit();
    }

    onInputKeydownEnter(e?: any) {
        if (this.autocompleteItems.length) {
            this.filterSuggestions(this.value);
        }
        this.onKeydown.emit({event: e, value: this.value});
    }

    onInputKeydownEscape(e?: any) {
        this.onEscape.emit(e);
    }

    filterSuggestions(value?: string) {
        if (value) {
            this.filteredSuggestions = (JSON.parse(JSON.stringify(this.autocompleteItems))).filter(item => item.toLowerCase().includes(value.toLowerCase()));
        } else {
            this.filteredSuggestions = JSON.parse(JSON.stringify(this.autocompleteItems));
        }

    }

    selectOption(item: string) {
        this.showSuggestion = false;
        this.value = item;
        this.filterSuggestions(this.value);
    }
}
